import React, { useCallback, useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

const formatCurrency = (val) => {
  const n = Number(val) || 0;
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const CategoryCard = ({ title, subtitle, color, stats = [] }) => (
  <div className="card" style={{ borderTop: `4px solid ${color}`, marginBottom: 20 }}>
    <div style={{ marginBottom: 14 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: 'var(--text-main)' }}>{title}</h3>
      <p style={{ fontSize: 11, color: 'var(--muted-light)', margin: '2px 0 0 0' }}>{subtitle}</p>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${stats.length}, 1fr)`, gap: 12 }}>
      {stats.map((s, idx) => (
        <div key={idx} style={{ background: 'var(--bg-card)', padding: '10px 12px', borderRadius: 6 }}>
          <div style={{ fontSize: 11, color: 'var(--muted-light)', marginBottom: 2 }}>{s.label}</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: s.color || 'var(--text-main)' }}>{s.value}</div>
        </div>
      ))}
    </div>
  </div>
);

const DashboardPage = () => {
  const { user, loading: authLoading } = useAuth();
  const [reportData, setReportData] = useState(null);
  const [freelancersCount, setFreelancersCount] = useState(0);
  const [employeesCount, setEmployeesCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, fRes, eRes] = await Promise.all([
        api.get('/api/admin/reports'),
        api.get('/api/admin/freelancers').catch(() => ({ data: { data: [] } })),
        api.get('/api/admin/employees').catch(() => ({ data: { data: [] } }))
      ]);

      setReportData(rRes.data.data || {});
      setFreelancersCount((fRes.data.data || []).length);
      setEmployeesCount((eRes.data.data || []).length);
    } catch (err) {
      console.error('loadDashboard error', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading) loadData();
  }, [authLoading, loadData]);

  const cStats = reportData?.clientStats || {};
  const fStats = reportData?.freelancerStats || {};
  const eStats = reportData?.employeeStats || {};

  const clientSummaryStats = [
    { label: 'Invoices Generated', value: cStats.total_invoices || 0 },
    { label: 'Total Receivable', value: formatCurrency(cStats.total_receivable) },
    { label: 'Received Revenue', value: formatCurrency(cStats.total_received), color: 'var(--success)' },
    { label: 'Outstanding / Overdue', value: formatCurrency(cStats.total_outstanding), color: 'var(--danger)' },
  ];

  const freelancerSummaryStats = [
    { label: 'Active Freelancers', value: freelancersCount },
    { label: 'Bills Received', value: fStats.total_bills || 0 },
    { label: 'Expenses Paid', value: formatCurrency(fStats.total_paid), color: 'var(--success)' },
    { label: 'Amount Pending', value: formatCurrency(fStats.total_pending), color: 'var(--danger)' },
  ];

  const employeeSummaryStats = [
    { label: 'Active Staff', value: employeesCount },
    { label: 'Payslips Issued', value: eStats.total_payslips || 0 },
    { label: 'Salary Paid', value: formatCurrency(eStats.total_paid), color: 'var(--success)' },
    { label: 'Salary Pending', value: formatCurrency(eStats.total_pending), color: 'var(--danger)' },
  ];

  const chartData = [
    { name: 'Client Revenue', Received: cStats.total_received || 0, Pending: cStats.total_outstanding || 0 },
    { name: 'Freelancer Expenses', Paid: fStats.total_paid || 0, Pending: fStats.total_pending || 0 },
    { name: 'Employee Payroll', Paid: eStats.total_paid || 0, Pending: eStats.total_pending || 0 },
  ];

  return (
    <div className="admin-page-container">
      <div className="page-header-actions" style={{ marginBottom: 24 }}>
        <div>
          <h1 className="page-title" style={{ fontFamily: 'Bebas Neue, sans-serif', fontSize: 28, letterSpacing: 2 }}>
            Agency Overview & Financial Categories
          </h1>
          <p className="page-subtitle">
            Welcome back, {user?.full_name?.split(' ')[0] || 'Admin'} • Isolated accounting dashboards for Client Revenue, Freelancer Expenses, and Employee Payroll
          </p>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={loadData} disabled={loading}>
          {loading ? (
            <>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, marginRight: 6, animation: 'spin 1s linear infinite' }}>
                <path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                <path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
              </svg>
              Refreshing...
            </>
          ) : (
            <>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, marginRight: 6 }}>
                <path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                <path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
              </svg>
              Refresh Data
            </>
          )}
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: 'center' }}><div className="spinner" style={{ width: 32, height: 32 }} /></div>
      ) : (
        <>
          {/* SEPARATE ACCOUNTING CATEGORY SUMMARIES */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
            {/* 1. CLIENTS */}
            <CategoryCard
              title="1. CLIENT REVENUE & INVOICES"
              subtitle="Services provided by WebDigiting to clients"
              color="#FFB400"
              stats={clientSummaryStats}
            />

            {/* 2. FREELANCERS */}
            <CategoryCard
              title="2. FREELANCER VENDOR EXPENSES"
              subtitle="Independent contractors billing WebDigiting for services"
              color="#3b82f6"
              stats={freelancerSummaryStats}
            />

            {/* 3. EMPLOYEES */}
            <CategoryCard
              title="3. EMPLOYEE PAYROLL & SALARIES"
              subtitle="WebDigiting internal staff payroll and payslips"
              color="#10b981"
              stats={employeeSummaryStats}
            />
          </div>

          {/* COMPARATIVE CATEGORY CHART */}
          <div className="card" style={{ marginTop: 20 }}>
            <h3 style={{ fontSize: 16, marginBottom: 15 }}>Financial Category Breakdown (INR)</h3>
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: '#131722', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, fontSize: 12, color: '#f8fafc' }} />
                  <Legend verticalAlign="top" height={36} />
                  <Bar dataKey="Received" fill="#10b981" name="Received / Settled" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Paid" fill="#3b82f6" name="Expenses Paid" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Pending" fill="#f43f5e" name="Pending / Outstanding" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardPage;
